import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MusicRegistrationHeader from '@/components/Layout/MusicRegistrationHeader';
import LoginModal from '@/components/Modal/LoginModal';
import MusicParentRegistrationModal from '@/components/Registration/MusicParentRegistrationModal';
import MusicTutorRegistrationModal from '@/components/Registration/MusicTutorRegistrationModal';
import { getMusicRegistrationCompletionPath } from '@/types/musicRegistrationFlow';
import { getPendingRegistration } from '@/utils/storage';

type MusicRegistrationType = 'music_tutor' | 'music_parent';

const getPathRegistrationType = (pathname: string): MusicRegistrationType | null => {
  if (pathname.endsWith('/tutor')) return 'music_tutor';
  if (pathname.endsWith('/parent')) return 'music_parent';
  return null;
};

/**
 * Owns the complete page-based Music registration lifecycle.
 *
 * The matched wildcard route remains mounted while the URL moves between the
 * form and authentication steps. This deliberately mirrors the Academic modal
 * lifecycle: the form component is hidden during auth, not unmounted, so its
 * React state remains the primary draft. sessionStorage is only the reload
 * recovery layer.
 */
const MusicRegistrationFlowPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathRegistrationType = getPathRegistrationType(location.pathname);
  const [registrationType, setRegistrationType] = useState<MusicRegistrationType | null>(() => {
    const pending = getPendingRegistration();
    if (pathRegistrationType) return pathRegistrationType;
    return pending?.type === 'music_tutor' || pending?.type === 'music_parent' ? pending.type : null;
  });
  const isAuthenticationStep = location.pathname.endsWith('/auth');

  useEffect(() => {
    if (pathRegistrationType) setRegistrationType(pathRegistrationType);
  }, [pathRegistrationType]);

  useEffect(() => {
    if (isAuthenticationStep && !registrationType) {
      navigate(getMusicRegistrationCompletionPath('session-unavailable'), { replace: true });
    }
  }, [isAuthenticationStep, navigate, registrationType]);

  const isTutorRegistration = registrationType === 'music_tutor';
  const registrationPath = isTutorRegistration ? '/music/register/tutor' : '/music/register/parent';
  const loginOptions = useMemo(() => ({
    initialMode: 'signup' as const,
    initialUserType: isTutorRegistration ? 'tutor' as const : 'parent' as const,
    fromRegistration: true,
    registrationService: 'music' as const,
  }), [isTutorRegistration]);

  if (!registrationType) return null;

  return (
    <div className="theme-music min-h-screen bg-[#fffdf9] font-redesign-body text-gray-900">
      <MusicRegistrationHeader />
      <main className="px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
        {isTutorRegistration ? (
          <MusicTutorRegistrationModal
            isOpen={!isAuthenticationStep}
            onClose={() => undefined}
            onCompleted={() => navigate(getMusicRegistrationCompletionPath('tutor-submitted'), { replace: true })}
            presentation="page"
            showPageBackButton={false}
            successFeedback="none"
          />
        ) : (
          <MusicParentRegistrationModal
            isOpen={!isAuthenticationStep}
            onClose={() => undefined}
            onCompleted={() => navigate(getMusicRegistrationCompletionPath('parent-submitted'), { replace: true })}
            presentation="page"
            showPageBackButton={false}
            successFeedback="none"
          />
        )}

        <LoginModal
          isOpen={isAuthenticationStep}
          onClose={() => undefined}
          presentation="page"
          showPageBackButton
          onBackToRegistration={() => navigate(registrationPath, { replace: true })}
          onRegistrationResolved={isTutorRegistration ? ({ status }) => {
            navigate(getMusicRegistrationCompletionPath(status === 'approved' ? 'tutor-approved' : 'tutor-pending'), {
              replace: true,
            });
          } : undefined}
          options={loginOptions}
        />
      </main>
    </div>
  );
};

export default MusicRegistrationFlowPage;
